import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Tabs, Tab, Table, Row, Col, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { 
  getUserActivities, 
  getUserActivityAnalytics, 
  getActivitiesForUser,
  testUserActivityApi
} from '../../Service/userActivityService';
import Chart from 'chart.js/auto';
import { Bar, Pie } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import { selectLoggedInUserId } from '../../Store/features/user/userSlice';

const UserActivityDashboard = () => {
  const { userId } = useParams();
  const loggedInUserId = useSelector(selectLoggedInUserId);
  const isViewingOwnActivity = !userId || userId === loggedInUserId;
  
  const [activities, setActivities] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0], // today
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [apiTestResult, setApiTestResult] = useState(null);
  const [apiTestLoading, setApiTestLoading] = useState(false);

  // Fetch user activities
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const queryParams = {
          page: pagination.page,
          limit: pagination.limit,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        };

        let activitiesResponse;
        
        if (isViewingOwnActivity) {
          // Get current user's activities
          activitiesResponse = await getUserActivities(queryParams);
        } else {
          // Get another user's activities (admin view)
          activitiesResponse = await getActivitiesForUser(userId, queryParams);
        }

        if (activitiesResponse && activitiesResponse.data) {
          setActivities(activitiesResponse.data);
          setPagination(activitiesResponse.pagination || pagination);
        } else {
          console.warn('UserActivityDashboard: No activity data received');
          setActivities([]);
        }

        // Fetch analytics - for now we can only get analytics for the current user
        // In the future, the backend could be updated to support getting analytics for any user
        if (isViewingOwnActivity) {
          const analyticsResponse = await getUserActivityAnalytics({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          });

          if (analyticsResponse && analyticsResponse.data) {
            setAnalytics(analyticsResponse.data);
          } else {
            console.warn('UserActivityDashboard: No analytics data received');
            setAnalytics(null);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('UserActivityDashboard: Error fetching data:', err);
        setError('Failed to load user activity data: ' + (err.message || 'Unknown error'));
        setLoading(false);
      }
    };

    fetchData();
  }, [pagination.page, pagination.limit, dateRange.startDate, dateRange.endDate, userId, loggedInUserId, isViewingOwnActivity]);

  // Handle date range change
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle pagination change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination((prev) => ({
        ...prev,
        page: newPage,
      }));
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Prepare chart data for pages visited
  const preparePageVisitData = () => {
    if (!analytics || !analytics.pagesVisited || analytics.pagesVisited.length === 0) {
      return null;
    }

    const labels = analytics.pagesVisited.map((item) => item._id);
    const data = analytics.pagesVisited.map((item) => item.count);

    return {
      labels,
      datasets: [
        {
          label: 'Page Visits',
          data,
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    };
  };

  // Prepare chart data for action types
  const prepareActionTypeData = () => {
    if (!analytics || !analytics.actionsPerformed || analytics.actionsPerformed.length === 0) {
      return null;
    }

    const labels = analytics.actionsPerformed.map((item) => item._id);
    const data = analytics.actionsPerformed.map((item) => item.count);

    return {
      labels,
      datasets: [
        {
          label: 'Actions Performed',
          data,
          backgroundColor: [
            'rgba(255, 99, 132, 0.2)',
            'rgba(54, 162, 235, 0.2)',
            'rgba(255, 206, 86, 0.2)',
            'rgba(75, 192, 192, 0.2)',
            'rgba(153, 102, 255, 0.2)',
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  // Function to test API connectivity
  const handleTestApi = async () => {
    setApiTestLoading(true);
    setApiTestResult(null);
    
    try {
      const result = await testUserActivityApi();
      setApiTestResult(result);
    } catch (error) {
      setApiTestResult({
        success: false,
        message: `Test failed: ${error.message}`
      });
    } finally {
      setApiTestLoading(false);
    }
  };

  return (
    <div className="user-activity-dashboard p-3">
      <h2>
        {isViewingOwnActivity 
          ? "My Activity Dashboard" 
          : "User Activity Dashboard"}
      </h2>

      {!isViewingOwnActivity && (
        <Alert variant="info">
          You are viewing activity data for another user (ID: {userId}).
        </Alert>
      )}

      {/* Diagnostic Card */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Diagnostics</h5>
          <Row>
            <Col>
              <Button 
                variant="info" 
                onClick={handleTestApi} 
                disabled={apiTestLoading}
              >
                {apiTestLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="ms-2">Testing API...</span>
                  </>
                ) : (
                  'Test API Connection'
                )}
              </Button>
              
              {apiTestResult && (
                <Alert 
                  variant={apiTestResult.success ? 'success' : 'danger'}
                  className="mt-3"
                >
                  <p><strong>Status:</strong> {apiTestResult.success ? 'Success' : 'Failed'}</p>
                  <p><strong>Message:</strong> {apiTestResult.message}</p>
                  {apiTestResult.data && (
                    <pre className="mt-2">
                      {JSON.stringify(apiTestResult.data, null, 2)}
                    </pre>
                  )}
                </Alert>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Date Range Card */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Date Range</h5>
          <Form>
            <Row>
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={dateRange.startDate}
                    onChange={handleDateRangeChange}
                  />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={dateRange.endDate}
                    onChange={handleDateRangeChange}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={() => {
                    // Reset pagination to page 1 when filtering
                    setPagination((prev) => ({
                      ...prev,
                      page: 1,
                    }));
                  }}
                >
                  Apply
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <Tabs
          activeKey={activeTab}
          onSelect={(key) => setActiveTab(key)}
          className="mb-4"
        >
          <Tab eventKey="overview" title="Overview">
            {!isViewingOwnActivity ? (
              <Alert variant="warning" className="mb-4">
                Analytics overview is only available for your own activity. 
                Use the Activity Log tab to view this user's activities.
              </Alert>
            ) : analytics ? (
              <>
                <Row>
                  <Col md={6} className="mb-4">
                    <Card>
                      <Card.Body>
                        <h5>Pages Visited</h5>
                        {analytics.pagesVisited && analytics.pagesVisited.length > 0 ? (
                          <Bar data={preparePageVisitData()} />
                        ) : (
                          <p className="text-muted">No page visit data available</p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6} className="mb-4">
                    <Card>
                      <Card.Body>
                        <h5>Actions Performed</h5>
                        {analytics.actionsPerformed && analytics.actionsPerformed.length > 0 ? (
                          <Pie data={prepareActionTypeData()} />
                        ) : (
                          <p className="text-muted">No action data available</p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                <Card className="mb-4">
                  <Card.Body>
                    <h5>Summary</h5>
                    <Row>
                      <Col md={4}>
                        <div className="text-center">
                          <h6>Total Sessions</h6>
                          <h2>{analytics.totalSessions || 0}</h2>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="text-center">
                          <h6>Page Views</h6>
                          <h2>
                            {analytics.actionsPerformed
                              ? analytics.actionsPerformed.find((item) => item._id === 'PAGE_VIEW')?.count || 0
                              : 0}
                          </h2>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="text-center">
                          <h6>Button Clicks</h6>
                          <h2>
                            {analytics.actionsPerformed
                              ? analytics.actionsPerformed.find((item) => item._id === 'BUTTON_CLICK')?.count || 0
                              : 0}
                          </h2>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </>
            ) : (
              <Alert variant="info">
                No analytics data available. Try adjusting the date range or make sure you have activities recorded.
              </Alert>
            )}
          </Tab>
          <Tab eventKey="activities" title="Activity Log">
            <Card>
              <Card.Body>
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>Activity Type</th>
                      <th>Page</th>
                      <th>Component</th>
                      <th>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities && activities.length > 0 ? (
                      activities.map((activity) => (
                        <tr key={activity._id}>
                          <td>{activity.activityType}</td>
                          <td>{activity.page}</td>
                          <td>{activity.componentName || '-'}</td>
                          <td>{formatDate(activity.createdAt)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center">
                          No activities found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                {pagination && pagination.pages > 1 && (
                  <div className="d-flex justify-content-center mt-4">
                    <Button
                      variant="outline-primary"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="mx-1"
                    >
                      Previous
                    </Button>
                    <div className="mx-2 my-auto">
                      Page {pagination.page} of {pagination.pages}
                    </div>
                    <Button
                      variant="outline-primary"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className="mx-1"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      )}
    </div>
  );
};

export default UserActivityDashboard; 